// TQ #960: EINE Begriffsdefinition fuer Token-Buchungszeilen im Cockpit. Dashboard-Karte
// ("Token-Kontingent", Drilldown) und Seite "Token-Verlauf" zeigen dieselbe ERPNext-Buchung
// (Token-Buchung) und benennen sie gleich -- und wie der Token-Regiebericht
// (erp-workspace tools/abrechnung/regiebericht_beleg.py, Patric 25.10.2026).
//
//   Spalte      Feld in Token-Buchung   Anzeige
//   Datum       datum
//   Typ         typ                     COGS -> KI-Verbrauch, Labor -> Arbeitszeit, Infra -> sonstiger Verbrauch
//   Taetigkeit  taetigkeit              kundentaugliches Feld (max. 80 Zeichen, wie Regiebericht); "–" wenn leer,
//                                       "Korrektur" wenn leer UND Gegenbuchung (siehe isGegenbuchung)
//   Projekt     projekt                 optional (nur Token-Verlauf)
//   Beleg       beleg                   optional (nur Drilldown)
//   Menge       einheiten_mio           IT-SCE-Token in Mio
//
// Die interne Kategorie (Freitext) wird bewusst nicht angezeigt: der Regiebericht kennt sie nicht
// (Patric 25.10. 16:55). notizen bleibt ohnehin zu (TQ #509).

/** Typ-Bezeichnungen wie im Regiebericht; unbekannte Typen bleiben unveraendert sichtbar. */
export const TYP_LABEL: Record<string, string> = {
  COGS: 'KI-Verbrauch',
  Labor: 'Arbeitszeit',
  Infra: 'sonstiger Verbrauch',
}

export interface TokenBookingRaw {
  datum: string
  typ?: string
  kategorie?: string
  taetigkeit?: string
  beleg?: string
  projekt?: string
  einheiten_mio: number
}

export interface TokenBookingRow {
  key: string
  datum: string
  typ: string
  /** Leer, wenn der Endpunkt keinen Buchungstext liefert; "Korrektur" bei Gegenbuchung ohne Text */
  taetigkeit: string
  beleg?: string
  projekt?: string
  einheitenMio: number
}

/** Spaltenreihenfolge und -namen, fuer beide Ansichten identisch (Projekt/Beleg optional dazwischen). */
export const TOKEN_BOOKING_COLUMNS = ['Datum', 'Typ', 'Tätigkeit', 'Menge'] as const

export const MAX_TAETIGKEIT_LENGTH = 300
export const EMPTY_CELL = '–'

/** Buchungstext bereinigen: Whitespace/Zeilenumbrueche zu einem Leerzeichen, auf feste Laenge kappen.
 *  Wird nur als Text gerendert (React escaped), nie als HTML. */
export function normalizeTaetigkeit(text: string | undefined | null): string {
  if (!text) return ''
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length > MAX_TAETIGKEIT_LENGTH ? `${flat.slice(0, MAX_TAETIGKEIT_LENGTH - 1)}…` : flat
}

export const KORREKTUR_TEXT = 'Korrektur'

/** Typen, die kein Verbrauch sind und deshalb nie als Gegenbuchung gelten (Regiebericht: Aufladung). */
const KEIN_VERBRAUCH_TYPEN = new Set(['Aufladung'])

/** Gegenbuchung/Korrekturbuchung: Menge < 0 (erp-workspace tools/abrechnung/buchungsfelder.py
 *  gegenbuchung_felder: "Gegenbuchung (Menge < 0)"; regiebericht_beleg.py nimmt Menge < 0 als
 *  Gegenbuchung UNABHAENGIG von korrektur_zu). korrektur_zu selbst liefert der Cockpit-Endpunkt
 *  nicht aus. Aufladungen sind ausgenommen, falls sie je mit negativer Menge kommen. */
export function isGegenbuchung(raw: Pick<TokenBookingRaw, 'typ' | 'einheiten_mio'>): boolean {
  return typeof raw.einheiten_mio === 'number' && raw.einheiten_mio < 0 && !KEIN_VERBRAUCH_TYPEN.has(raw.typ?.trim() ?? '')
}

export function toTokenBookingRow(raw: TokenBookingRaw, index: number): TokenBookingRow {
  return {
    key: raw.beleg || `${raw.datum}-${index}`,
    datum: raw.datum,
    typ: TYP_LABEL[raw.typ?.trim() ?? ''] ?? (raw.typ?.trim() || EMPTY_CELL),
    taetigkeit: normalizeTaetigkeit(raw.taetigkeit) || (isGegenbuchung(raw) ? KORREKTUR_TEXT : ''),
    beleg: raw.beleg || undefined,
    projekt: raw.projekt || undefined,
    einheitenMio: raw.einheiten_mio,
  }
}

export function formatBookingDate(iso?: string): string {
  if (!iso) return EMPTY_CELL
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function formatBookingMio(mio: number): string {
  return `${mio.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Mio Token`
}
